<script setup>
import { ref, computed, defineAsyncComponent, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
/***/
import { useApplicationStore } from '@stores/application';
import { useAuthRunningTaskStore } from '@stores/auth/runningTask';
/***/
import { useAuthUser } from '@compositions/Auth';
/**/
import UserModel from '@models/UserModel';

// Composants
const Loader = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/UI/Loader'));
const Layout = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Layouts/Authenticated'));
const IncomingTaskList = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Me/Task/Running/Incoming/List'));
const TaskList = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Me/Task/Running/List/Table/Wrapper'));

// Router
const router = useRouter();
const route = useRoute();

// I18n
const { t } = useI18n();

// Store
const applicationStore = useApplicationStore();
const runningTaskStore = useAuthRunningTaskStore();

const currentApplication = computed(() => applicationStore.getCurrent);

// Tâche d'où vient l'utilisateur
const fromProcessingTaskPublicId = (route.query['from-processing-task']);
const fromProcessingTask = computed((() => {
    if(! fromProcessingTaskPublicId) {
        return null;
    }
    return runningTaskStore.getRunningTaskByPublicId(fromProcessingTaskPublicId);
}));

// Liste des tâches à venir
const incomingTasks = computed(() => runningTaskStore.getIncomingRunningTasks);

const { authUser, authUserHasPermission } = useAuthUser({});
const allowed = ref(false);

// Chargement des tâches à venir
const loadingIncomingTasks = ref(false);
const loadIncomingTasksError = ref(false);
const loadIncomingTasks = (() => {

    if(loadingIncomingTasks.value) {
        return false;
    }

    loadingIncomingTasks.value = true;
    loadIncomingTasksError.value = false;

    runningTaskStore.loadIncomingRunningTasks(3).then(() => {
        // Géré par le store
    }).catch(() => {
        loadIncomingTasksError.value = true;
    }).finally(() => {
        loadingIncomingTasks.value = false;
    });

});

// Retourne si l'utilisateur est un agent
const isAgent = computed(() => {
    if(! authUser.value) {
        return false;
    }
    return (new UserModel(authUser.value)).isAgent();
});

onMounted(() => {

    const canConsult = (authUserHasPermission('consult tasks') && isAgent.value);
    if(! canConsult) {
        router.push({
            name: 'error',
            params: {
                statusCode: 404
            }
        });
    } else {
        loadIncomingTasks();
        allowed.value = true;
        document.title = t('page.me.task.running.list.title') + ' - ' + currentApplication.value.name;
    }
});
</script>

<template>
    <layout v-if="allowed">
        <loader v-if="loadingIncomingTasks"></loader>
        <template v-else>
            <div class="content_header">
                <div class="content-header_title">
                    <h1 class="first-title">{{ $t('page.me.task.running.list.title') }}</h1>
                    <p>{{ $t('page.me.task.running.list.description') }}</p>
                </div>
            </div>

            <p
                v-if="loadIncomingTasksError"
                class="generic-message generic-message-error"
            >
                {{ $t('page.me.task.running.list.incoming.loading.error') }}
            </p>

            <p v-if="fromProcessingTask" class="generic-message generic-message-success">
                {{ $t('page.me.task.running.list.processing.complete', {
                    task: fromProcessingTask.title,
                    customer: fromProcessingTask.customer.fullName
                }) }}
            </p>

            <incoming-task-list v-if="incomingTasks.length > 0" :tasks="incomingTasks"></incoming-task-list>
            <task-list></task-list>
        </template>
    </layout>
</template>
